import React, { useState, useMemo, useId } from 'react';
import FundraisingSimulationClient from '../../../models/fundraisingSimulations/fundraisingSimulationClient';
import { SimulationResultData } from './SimulatorView';

export type Option = [string, string];

export interface Tag {
  id: number;
  name: string;
  category: string;
  ecf: number;
  plus: number;
  ecf_order: number;
  plus_order: number;
  [key: string]: string | number; // tagSortKeyの動的プロパティ用
}

export type SortableTagKeys = 'ecf_order' | 'plus_order';

interface Props {
  type: string;
  tagSortKey: SortableTagKeys;
  presidentAgeOptions: Option[];
  birthOnOptions: Option[];
  businessTypeOptions: Option[];
  requestedFundingAmountOptions: Option[];
  totalFundingAmountOptions: Option[];
  currentValuationOptions: Option[];
  previousPeriodSalesOptions: Option[];
  financialStatusOptions: Option[];
  fundraisingSimulationTags: Tag[];
  onShowResults: (data: SimulationResultData) => void;
}

interface FormData {
  presidentAge: string;
  birthOn: string;
  businessType: string;
  requestedFundingAmount: string;
  totalFundingAmount: string;
  currentValuation: string;
  previousPeriodSales: string;
  financialStatus: string;
}

interface Errors {
  presidentAge?: string;
  birthOn?: string;
  businessType?: string;
  requestedFundingAmount?: string;
  totalFundingAmount?: string;
  currentValuation?: string;
  previousPeriodSales?: string;
  financialStatus?: string;
  presidentTag?: string;
  employeeTag?: string;
  companyStatusTag?: string;
  financialStatusTag?: string;
  shareholderTag?: string;
  [key: string]: string | undefined;
}

interface SelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  error: string | undefined;
}

const SelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  error,
}: SelectProps) => {
  // useId でインスタンスごとに一意な接頭辞を生成する（同一ページに ECF/Plus の
  // 2 フォームが並んでも id が衝突しない）。id は可読性のためのサフィックスとして残す。
  const fieldId = `${useId()}-${id}`;

  return (
    <div className="simulator__section__item select-block">
      <label
        htmlFor={fieldId}
        className="simulator__section__item-title required"
      >
        {label}
      </label>
      <div className="simulator__section__item-cont select-block">
        <div className={`simulator__select ${error ? 'is-invalid' : ''}`}>
          <select
            id={fieldId}
            value={value}
            onChange={e => onChange(e.target.value)}
            className={value === '' ? 'is-empty' : ''}
          >
            <option value="">選択してください</option>
            {options.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

interface TagButtonProps {
  label: string;
  tags: Tag[];
  category: string;
  disabled: boolean;
  selectedTagIds: number[];
  onChange: (value: number) => void;
  onReset: () => void;
  error: string | undefined;
}

const TagButton = ({
  label,
  tags,
  category,
  disabled,
  selectedTagIds,
  onChange,
  onReset,
  error,
}: TagButtonProps) => {
  const uid = useId();

  return (
    <div className="simulator__section__item">
      <div className="simulator__section__item-title required">
        {label}
        <span>(複数選択可)</span>
      </div>
      <div className="simulator__section__item-cont check-block">
        <fieldset
          className={`simulator__checkbox ${error ? 'is-invalid' : ''}`}
        >
          {tags.map(tag => (
            <label
              key={tag.id}
              htmlFor={`${uid}-checkbox-${tag.id}`}
              className={disabled ? 'disabled' : ''}
            >
              {tag.name}
              <input
                type="checkbox"
                id={`${uid}-checkbox-${tag.id}`}
                checked={selectedTagIds.includes(tag.id)}
                onChange={() => onChange(tag.id)}
                disabled={disabled}
              />
            </label>
          ))}
          <label htmlFor={`${uid}-reset-${category}`}>
            当てはまる項目がない
            <input
              type="checkbox"
              id={`${uid}-reset-${category}`}
              checked={disabled}
              onChange={() => onReset()}
            />
          </label>
        </fieldset>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

const DEFAULT_FORM_DATA: FormData = {
  presidentAge: '',
  birthOn: '',
  businessType: '',
  requestedFundingAmount: '',
  totalFundingAmount: '',
  currentValuation: '',
  previousPeriodSales: '',
  financialStatus: '',
};

// sessionStorage に診断（type 単位）でキャッシュしたフォーム状態を読み出す
const restoreCachedData = (type: string) =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 空文字も未保存として扱う既存挙動を維持する
  JSON.parse(sessionStorage.getItem(type) || '{}');

const SimulatorForm = (props: Props) => {
  const fundraisingSimulationClient = new FundraisingSimulationClient();

  // マウント時に sessionStorage[type] のキャッシュからフォーム状態を復元する。
  // type はマウント後に変化しないため、遅延初期化で読み込めば足りる。
  const [cachedData] = useState(() => restoreCachedData(props.type));
  const [formData, setFormData] = useState<FormData>(
    cachedData.formData ?? DEFAULT_FORM_DATA,
  );
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(
    cachedData.selectedTagIds ?? [],
  );
  const [disabledCategory, setDisabledCategory] = useState<string[]>(
    cachedData.disabledCategory ?? [],
  );
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState<boolean>(false);

  const {
    presidentTag,
    employeeTag,
    companyStatusTag,
    financialStatusTag,
    shareholderTag,
  } = useMemo(() => {
    const sortByCategory = (category: string) =>
      props.fundraisingSimulationTags
        .filter(tag => tag.category === category)
        .filter(tag => tag[props.tagSortKey] != null)
        .sort((a: Tag, b: Tag) => a[props.tagSortKey] - b[props.tagSortKey]);

    return {
      presidentTag: sortByCategory('1'),
      employeeTag: sortByCategory('2'),
      companyStatusTag: sortByCategory('3'),
      financialStatusTag: sortByCategory('4'),
      shareholderTag: sortByCategory('5'),
    };
  }, [props.fundraisingSimulationTags, props.tagSortKey]);

  const handleChange = (key: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[key as keyof Errors];
      return newErrors;
    });
  };

  const toggleSelection = (key: keyof Errors, id: number) => {
    setSelectedTagIds(prevSelected =>
      prevSelected.includes(id)
        ? prevSelected.filter(selectedId => selectedId !== id)
        : [...prevSelected, id],
    );

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[key];
      return newErrors;
    });
  };

  const handleResetTags = (tags: Tag[], key: string) => {
    const tagIdsToRemove = tags.map(tag => tag.id);
    setSelectedTagIds(prev => prev.filter(id => !tagIdsToRemove.includes(id)));

    setDisabledCategory(prev =>
      prev.includes(key)
        ? prev.filter(disabledCategory => disabledCategory !== key)
        : [...prev, key],
    );

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[key];
      return newErrors;
    });
  };

  const checkCategorySelected = (
    categoryTagList: Tag[],
    key: keyof Errors,
  ): boolean => {
    return (
      !categoryTagList.some(tag => selectedTagIds.includes(tag.id)) &&
      !disabledCategory.includes(key as string)
    );
  };

  const validate = () => {
    const newErrors: Errors = {};

    if (!formData.presidentAge)
      newErrors.presidentAge = '年齢を選択してください';
    if (!formData.birthOn) newErrors.birthOn = '設立年を選択してください';
    if (!formData.businessType)
      newErrors.businessType = '業界・業種を選択してください';
    if (!formData.requestedFundingAmount)
      newErrors.requestedFundingAmount = '調達希望金額を選択してください';
    if (!formData.totalFundingAmount)
      newErrors.totalFundingAmount = '累計資金調達総額を選択してください';
    if (!formData.currentValuation)
      newErrors.currentValuation = '現在バリュエーションを選択してください';
    if (!formData.previousPeriodSales)
      newErrors.previousPeriodSales = '前期売上高を選択してください';
    if (!formData.financialStatus)
      newErrors.financialStatus = '業績・財務を選択してください';

    if (checkCategorySelected(presidentTag, 'presidentTag'))
      newErrors.presidentTag = '経験・実績を選択してください';
    if (checkCategorySelected(employeeTag, 'employeeTag'))
      newErrors.employeeTag = '経験・実績を選択してください';
    if (checkCategorySelected(companyStatusTag, 'companyStatusTag'))
      newErrors.companyStatusTag = 'ステータス・特徴を選択してください';
    if (checkCategorySelected(financialStatusTag, 'financialStatusTag'))
      newErrors.financialStatusTag = 'IPO計画を選択してください';
    if (checkCategorySelected(shareholderTag, 'shareholderTag'))
      newErrors.shareholderTag = '資金調達実績を選択してください';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // 返却パラメータの加工用
  const toNumOrNull = (value: string | number): number | null => {
    const num = Number(value);
    return isNaN(num) || value === '' ? null : num;
  };

  const handleSimulate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (loading) return;
    if (!validate()) return;

    setLoading(true);

    fundraisingSimulationClient
      .simulate({
        type: props.type,
        fundraising_simulation: {
          president_age: toNumOrNull(formData.presidentAge),
          birth_on: toNumOrNull(formData.birthOn),
          business_type: toNumOrNull(formData.businessType),
          requested_funding_amount: toNumOrNull(
            formData.requestedFundingAmount,
          ),
          total_funding_amount: toNumOrNull(formData.totalFundingAmount),
          current_valuation: toNumOrNull(formData.currentValuation),
          previous_period_sales: toNumOrNull(formData.previousPeriodSales),
          financial_status: toNumOrNull(formData.financialStatus),
        },
        fundraising_simulation_tag: {
          ids: selectedTagIds,
        },
      })
      .then(response => {
        const responseResultData = {
          score: response.data.score,
          simulatedAmount: response.data.simulatedAmount,
          unit: response.data.unit,
          range: response.data.range,
        };

        // SessionStorageにキャッシュ
        sessionStorage.setItem(
          props.type,
          JSON.stringify({
            formData,
            selectedTagIds,
            disabledCategory,
            resultData: { ...responseResultData },
          }),
        );

        // 結果を親コンポーネントに渡す
        props.onShowResults(responseResultData);
      })
      .catch(error => {
        console.warn(error);
        alert('エラーが発生しました。ページを再読み込みしてください。');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {Object.keys(errors).length > 0 && (
        <div className="simulator__contents--error">
          未入力の必須項目があります。
        </div>
      )}
      <div className="simulator__section">
        <h4>代表について</h4>
        <SelectField
          id="presidentAge"
          label="年齢"
          value={formData.presidentAge}
          onChange={value => handleChange('presidentAge', value)}
          options={props.presidentAgeOptions}
          error={errors.presidentAge}
        />
        <TagButton
          label="経験・実績"
          tags={presidentTag}
          category="1"
          disabled={disabledCategory.includes('presidentTag')}
          selectedTagIds={selectedTagIds}
          onChange={value => toggleSelection('presidentTag', value)}
          onReset={() => handleResetTags(presidentTag, 'presidentTag')}
          error={errors.presidentTag}
        />
      </div>
      <div className="simulator__section">
        <h4>経営陣/幹部について</h4>
        <TagButton
          label="経験・実績"
          tags={employeeTag}
          category="2"
          disabled={disabledCategory.includes('employeeTag')}
          selectedTagIds={selectedTagIds}
          onChange={value => toggleSelection('employeeTag', value)}
          onReset={() => handleResetTags(employeeTag, 'employeeTag')}
          error={errors.employeeTag}
        />
      </div>
      <div className="simulator__section">
        <h4>設立年について</h4>
        <SelectField
          id="birthOn"
          label="設立年"
          value={formData.birthOn}
          onChange={value => handleChange('birthOn', value)}
          options={props.birthOnOptions}
          error={errors.birthOn}
        />
      </div>
      <div className="simulator__section">
        <h4>事業について</h4>
        <SelectField
          id="businessType"
          label="業界・業種"
          value={formData.businessType}
          onChange={value => handleChange('businessType', value)}
          options={props.businessTypeOptions}
          error={errors.businessType}
        />
        <TagButton
          label="ステータス・特徴"
          tags={companyStatusTag}
          category="3"
          disabled={disabledCategory.includes('companyStatusTag')}
          selectedTagIds={selectedTagIds}
          onChange={value => toggleSelection('companyStatusTag', value)}
          onReset={() => handleResetTags(companyStatusTag, 'companyStatusTag')}
          error={errors.companyStatusTag}
        />
      </div>
      <div className="simulator__section">
        <h4>ファイナンスについて</h4>
        <SelectField
          id="requestedFundingAmount"
          label="調達希望金額"
          value={formData.requestedFundingAmount}
          onChange={value => handleChange('requestedFundingAmount', value)}
          options={props.requestedFundingAmountOptions}
          error={errors.requestedFundingAmount}
        />
        <SelectField
          id="totalFundingAmount"
          label="累計資金調達総額"
          value={formData.totalFundingAmount}
          onChange={value => handleChange('totalFundingAmount', value)}
          options={props.totalFundingAmountOptions}
          error={errors.totalFundingAmount}
        />
        <SelectField
          id="currentValuation"
          label="現在バリュエーション"
          value={formData.currentValuation}
          onChange={value => handleChange('currentValuation', value)}
          options={props.currentValuationOptions}
          error={errors.currentValuation}
        />
        <SelectField
          id="previousPeriodSales"
          label="前期売上高"
          value={formData.previousPeriodSales}
          onChange={value => handleChange('previousPeriodSales', value)}
          options={props.previousPeriodSalesOptions}
          error={errors.previousPeriodSales}
        />
        <SelectField
          id="financialStatus"
          label="業績・財務"
          value={formData.financialStatus}
          onChange={value => handleChange('financialStatus', value)}
          options={props.financialStatusOptions}
          error={errors.financialStatus}
        />
        <TagButton
          label="IPO計画"
          tags={financialStatusTag}
          category="4"
          disabled={disabledCategory.includes('financialStatusTag')}
          selectedTagIds={selectedTagIds}
          onChange={value => toggleSelection('financialStatusTag', value)}
          onReset={() =>
            handleResetTags(financialStatusTag, 'financialStatusTag')
          }
          error={errors.financialStatusTag}
        />
        <TagButton
          label="資金調達実績"
          tags={shareholderTag}
          category="5"
          disabled={disabledCategory.includes('shareholderTag')}
          selectedTagIds={selectedTagIds}
          onChange={value => toggleSelection('shareholderTag', value)}
          onReset={() => handleResetTags(shareholderTag, 'shareholderTag')}
          error={errors.shareholderTag}
        />
      </div>

      <a
        className={`diagnosis-result-btn ${loading ? 'disabled' : ''}`}
        onClick={loading ? undefined : handleSimulate}
      >
        診断結果をみる
      </a>
    </>
  );
};

export default SimulatorForm;
